import React, { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';
import useWishlist from '@awyes/cms/hooks/useWishlist';
import Image from '@awyes/cms/components/Image';
import useSearchProductPage from '../hooks/awyes/useSearchProductPage';
import SearchSort from './SearchSort';
import Spinner from "./Spinner";
import Button from "./Button";
import { formatPrice } from '../lib/formatters';

import '../scss/SearchResults.scss';

const SearchResults = ({ categoryId, preselect, filters, searchBy, orderBy, sort, setSort }) => {
  const [skipFetch, setSkipFetch] = useState(preselect?.length > 0);
  const [offset, setOffset] = useState(undefined);
  const [results, setResults] = useState([]);
  const [{ loading, data }, { request }] = useSearchProductPage();
  const [{ wishlist }, { wishlistToggle }] = useWishlist();

  const fetchProducts = useCallback(async function doRequest(offset) {
    if (categoryId?.length && !skipFetch) {
      request.aborted = false;
      const productType = Object.keys(filters ?? {}).map((key) => ({ id: filters[key].id, typeName: filters[key].typeName }));
      if (productType.length) {
        await request({ filter: { primary: { categoryId }, field: { productType } }, searchBy, orderBy, offset }, () => doRequest.aborted);
      } else {
        await request({ filter: { primary: { categoryId } }, searchBy, orderBy, offset }, () => doRequest.aborted);
      }
    }
  }, [request, categoryId, filters, searchBy, orderBy, skipFetch]);

  useEffect(() => {
    if (skipFetch && Object.keys(filters ?? {}).length) {
      setSkipFetch(false);
    }
  }, [skipFetch, filters])

  useEffect(() => {
    if (categoryId?.length) {
      setOffset(undefined);
      setResults([]);
    }
  }, [categoryId, filters, sort]);

  useEffect(() => {
    fetchProducts();
    return () => {
      fetchProducts.aborted = true;
    };
  }, [fetchProducts]);

  useEffect(() => {
    if (data) {
      setOffset(data.nextToken);
    }
    if (data?.items?.length) {
      setResults((old) => [...old, ...data.items]);
    }
  }, [data]);

  return (
    <div className="search-results">
      <div className="search-results__header">
        <SearchSort sort={sort} setSort={setSort} />
      </div>
      <div className="search-results__items">
        { !loading && !results?.length ? <div className="search-results__empty">No items found, please try a different filter</div> : null }
        {results.map(({ awyesId, url, name, price, wasPrice, images: [{ thumbPath, width, height } = {}] = [] }) =>
          <figure data-awyesid={awyesId} key={awyesId} className="search-results__item">
            <NavLink className="search-results__item__link" to={url}>
              <div className={classNames('search-results__item__image-container')}>
                <Image className="search-results__item__image" width={width} height={height} loading="lazy" path={thumbPath} alt={name} />
              </div>
              <figcaption className="search-results__item__content">
                <div className="search-results__item__details">
                  <span>{name}</span>
                  <button
                    className={classNames('search-results__item__wishlist', { 'search-results__item__wishlist--selected': wishlist?.includes(awyesId) })}
                    onClick={(e) => {
                      e.preventDefault();
                      wishlistToggle(awyesId);
                    }}
                  />
                </div>
                <div className={classNames('search-results__item__prices', { 'search-results__item__prices--with-was': wasPrice })}>
                  {wasPrice ? <span className="search-results__item__was-price">{formatPrice(wasPrice)}</span> : null}
                  <span className="search-results__item__price">{formatPrice(price)}</span>
                </div>
              </figcaption>
            </NavLink>
          </figure>
        )}
      </div>
      <div className="search-results__footer">
        { loading ? <Spinner className="search-results__spinner" /> : null }
        { !loading && offset ? <Button className="search-results__show-more" onClick={() => fetchProducts(offset)}>Show More</Button> : null }
      </div>
    </div>
  );
};

export default SearchResults;